import { ToolItem } from "../types";

export interface PdfToolItem extends ToolItem {
  pdfCategory:
    | "organize"
    | "convert"
    | "edit"
    | "optimize"
    | "security"
    | "view-extract"
    | "other";
  pdfCategoryLabel: string;
  iconBgColor: string; // Tailwind class for background
  iconTextColor: string; // Tailwind class for icon text
  iconName: string;
}

export const PDF_CATEGORIES_TABS = [
  { id: "all", label: "All PDF Tools", icon: "Grid" },
  { id: "organize", label: "Organize PDF", icon: "Folder" },
  { id: "convert", label: "Convert PDF", icon: "RefreshCw" },
  { id: "edit", label: "Edit PDF", icon: "Edit3" },
  { id: "optimize", label: "Optimize PDF", icon: "Sliders" },
  { id: "security", label: "Security PDF", icon: "ShieldCheck" },
  { id: "view-extract", label: "View & Extract", icon: "Eye" },
  { id: "other", label: "Other PDF Tools", icon: "SlidersHorizontal" },
];

export const PDF_TOOLS_DATA: PdfToolItem[] = [
  // --- OPTIMIZE & EDIT PDF ---
  // --- ORGANIZE PDF (10 Tools) ---

  {
    id: "pdf-merger",
    name: "PDF Merger",
    description: "Merge multiple PDF files into one single organized document.",
    pdfCategory: "organize",
    pdfCategoryLabel: "Organize PDF",
    iconBgColor: "bg-red-500/15",
    iconTextColor: "text-red-500",
    iconName: "FileText",
    isPopular: true,
    actionType: "generic",
  },
  {
    id: "pdf-extractor",
    name: "PDF Page Extractor",
    description:
      "Extract specific pages from a PDF and save them as a new document.",
    pdfCategory: "organize",
    pdfCategoryLabel: "Organize PDF",
    iconBgColor: "bg-blue-600",
    iconTextColor: "text-white",
    iconName: "FileOutput",
    isPopular: true,
    actionType: "generic",
  },
  {
    id: "pdf-splitter",
    name: "PDF Splitter",
    description:
      "Split a PDF into multiple separate files by page ranges or individual pages.",
    pdfCategory: "organize",
    pdfCategoryLabel: "Organize PDF",
    iconBgColor: "bg-orange-500/15",
    iconTextColor: "text-orange-500",
    iconName: "Scissors",
    isPopular: true,
    actionType: "generic",
  },
  {
    id: "pdf-rotator",
    name: "PDF Rotator",
    description:
      "Rotate PDF pages clock-wise or counter-clockwise (90°, 180°, 270°).",
    pdfCategory: "organize",
    pdfCategoryLabel: "Organize PDF",
    iconBgColor: "bg-orange-500",
    iconTextColor: "text-emerald-500",
    iconName: "RotateCw",
    actionType: "generic",
  },
  {
    id: "pdf-page-remover",
    name: "PDF Page Remover",
    description:
      "Select and permanently remove unwanted pages from your PDF file.",
    pdfCategory: "organize",
    pdfCategoryLabel: "Organize PDF",
    iconBgColor: "bg-red-500/15",
    iconTextColor: "text-red-500",
    iconName: "Trash2",
    actionType: "generic",
  },
  {
    id: "pdf-reorder-pages",
    name: "PDF Reorder Pages",
    description:
      "Drag and drop to rearrange pages in your PDF file effortlessly.",
    pdfCategory: "organize",
    pdfCategoryLabel: "Organize PDF",
    iconBgColor: "bg-blue-500/15",
    iconTextColor: "text-purple-500",
    iconName: "Move",
    actionType: "generic",
  },
  {
    id: "pdf-crop",
    name: "PDF Crop",
    description:
      "Crop PDF pages visually to trim margins, borders, or header elements.",
    pdfCategory: "organize",
    pdfCategoryLabel: "Organize PDF",
    iconBgColor: "bg-green-500/15",
    iconTextColor: "text-green-500",
    iconName: "Crop",
    actionType: "generic",
  },
  {
    id: "pdf-duplicate-page-finder",
    name: "PDF Duplicate Page Finder",
    description:
      "Identify and eliminate exact duplicate pages inside your PDF.",
    pdfCategory: "organize",
    pdfCategoryLabel: "Organize PDF",
    iconBgColor: "bg-blue-500/15",
    iconTextColor: "text-blue-500",
    iconName: "Copy",
    actionType: "generic",
  },
  {
    id: "pdf-blank-page-remover",
    name: "PDF Blank Page Remover",
    description:
      "Automatically scan, detect, and purge empty or nearly blank pages from PDF documents.",
    pdfCategory: "organize",
    pdfCategoryLabel: "Organize PDF",
    iconBgColor: "bg-pink-600",
    iconTextColor: "text-white",
    iconName: "FileX",
    isPopular: true,
    actionType: "generic",
  },
  {
    id: "pdf-page-number-adder",
    name: "PDF Page Number Adder",
    description:
      "Stamp custom page numbering with position and font formatting.",
    pdfCategory: "organize",
    pdfCategoryLabel: "Organize PDF",
    iconBgColor: "bg-fuchsia-500/15",
    iconTextColor: "text-fuchsia-500",
    iconName: "ListOrdered",
    actionType: "generic",
  },

  // --- CONVERT PDF (4 Tools) ---
  {
    id: "pdf-to-images",
    name: "PDF to Images",
    description:
      "Convert PDF pages into high-resolution PNG, JPG, or WebP graphics.",
    pdfCategory: "convert",
    pdfCategoryLabel: "Convert PDF",
    iconBgColor: "bg-orange-500/15",
    iconTextColor: "text-orange-500",
    iconName: "Image",
    isPopular: true,
    actionType: "generic",
  },
  {
    id: "images-to-pdf",
    name: "Images to PDF",
    description:
      "Bundle multiple PNG, JPG, or GIF image files into a formatted PDF.",
    pdfCategory: "convert",
    pdfCategoryLabel: "Convert PDF",
    iconBgColor: "bg-teal-500/15",
    iconTextColor: "text-teal-500",
    iconName: "FileImage",
    isPopular: true,
    actionType: "generic",
  },
  {
    id: "pdf-grayscale-converter",
    name: "PDF Grayscale Converter",
    description:
      "Transform colorful PDF pages to monochrome black & white for eco-printing.",
    pdfCategory: "convert",
    pdfCategoryLabel: "Convert PDF",
    iconBgColor: "bg-red-600",
    iconTextColor: "text-white",
    iconName: "Contrast",
    actionType: "generic",
  },
  {
    id: "pdf-color-inverter",
    name: "PDF Color Inverter",
    description:
      "Invert page backgrounds and text colors for dark-mode night reading.",
    pdfCategory: "convert",
    pdfCategoryLabel: "Convert PDF",
    iconBgColor: "bg-amber-500/15",
    iconTextColor: "text-amber-500",
    iconName: "Moon",
    actionType: "generic",
  },

  // --- EDIT PDF (7 Tools) ---
  {
    id: "pdf-watermark",
    name: "PDF Watermark",
    description:
      "Add custom text or image watermarks with opacity and angle controls.",
    pdfCategory: "edit",
    pdfCategoryLabel: "Edit PDF",
    iconBgColor: "bg-blue-600",
    iconTextColor: "text-white",
    iconName: "Stamp",
    isPopular: true,
    actionType: "generic",
  },
  {
    id: "pdf-bookmark-viewer",
    name: "PDF Bookmark Viewer",
    description:
      "Inspect document structure, outline tree, and navigational bookmarks.",
    pdfCategory: "edit",
    pdfCategoryLabel: "Edit PDF",
    iconBgColor: "bg-gradient-to-tr from-blue-600 to-blue-700",
    iconTextColor: "text-white",
    iconName: "Bookmark",
    isPopular: true,
    actionType: "generic",
  },
  {
    id: "pdf-bookmark-editor",
    name: "PDF Bookmark Editor",
    description:
      "Add, edit, or remove outline bookmarks and table of contents links.",
    pdfCategory: "edit",
    pdfCategoryLabel: "Edit PDF",
    iconBgColor: "bg-gradient-to-tr from-blue-600 to-blue-700",
    iconTextColor: "text-white",
    iconName: "FileEdit",
    actionType: "generic",
  },
  {
    id: "pdf-signature-placeholder",
    name: "PDF Signature Placeholder",
    description:
      "Prepare digital signature fields and placeholders on agreement pages.",
    pdfCategory: "edit",
    pdfCategoryLabel: "Edit PDF",
    iconBgColor: "bg-gradient-to-tr from-amber-500 to-yellow-400",
    iconTextColor: "text-white",
    iconName: "PenTool",
    isPopular: true,
    actionType: "generic",
  },
  {
    id: "pdf-annotation-remover",
    name: "PDF Annotation Remover",
    description:
      "Strip comments, highlights, stamps, and drawn markups from PDF pages.",
    pdfCategory: "edit",
    pdfCategoryLabel: "Edit PDF",
    iconBgColor: "bg-emerald-500/15",
    iconTextColor: "text-emerald-500",
    iconName: "Eraser",
    isNew: true,
    actionType: "generic",
  },
  {
    id: "pdf-margin-viewer",
    name: "PDF Margin Viewer",
    description:
      "Measure precise page margin padding, bleed, and safe zones with live rulers.",
    pdfCategory: "edit",
    pdfCategoryLabel: "Edit PDF",
    iconBgColor: "bg-gradient-to-tr from-purple-600 to-indigo-600",
    iconTextColor: "text-white",
    iconName: "Ruler",
    isNew: true,
    actionType: "generic",
  },
  {
    id: "pdf-margin-remover",
    name: "PDF Margin Remover",
    description:
      "Trim excess outer white space margins from scanned or printed PDF pages.",
    pdfCategory: "edit",
    pdfCategoryLabel: "Edit PDF",
    iconBgColor: "bg-emerald-500/15",
    iconTextColor: "text-emerald-500",
    iconName: "Minimize",
    actionType: "generic",
  },

  // --- OPTIMIZE PDF (3 Tools) ---
  {
    id: "pdf-compressor-wasm",
    name: "PDF Compressor (WASM)",
    description:
      "Compress PDF size instantly in browser using WebAssembly engine.",
    pdfCategory: "optimize",
    pdfCategoryLabel: "Optimize PDF",
    iconBgColor: "bg-amber-500/15",
    iconTextColor: "text-amber-500",
    iconName: "Archive",
    isPopular: true,
    actionType: "generic",
  },
  {
    id: "pdf-orientation-fixer",
    name: "PDF Orientation Fixer",
    description:
      "Automatically detect and fix sideways or upside down scanned pages.",
    pdfCategory: "optimize",
    pdfCategoryLabel: "Optimize PDF",
    iconBgColor: "bg-teal-500/15",
    iconTextColor: "text-teal-500",
    iconName: "Compass",
    actionType: "generic",
  },
  {
    id: "pdf-size-calculator",
    name: "PDF Size Calculator",
    description:
      "Calculate detailed byte distribution, stream compression, and asset size.",
    pdfCategory: "optimize",
    pdfCategoryLabel: "Optimize PDF",
    iconBgColor: "bg-sky-500/15",
    iconTextColor: "text-sky-500",
    iconName: "Calculator",
    actionType: "generic",
  },

  // --- SECURITY PDF (2 Tools) ---
  {
    id: "pdf-unlock",
    name: "PDF Unlock (Password Removal)",
    description:
      "Decrypt and remove owner or open password protection from PDF documents.",
    pdfCategory: "security",
    pdfCategoryLabel: "Security PDF",
    iconBgColor: "bg-emerald-500/15",
    iconTextColor: "text-emerald-500",
    iconName: "Unlock",
    isPopular: true,
    actionType: "generic",
  },
  {
    id: "pdf-protect",
    name: "PDF Protect",
    description:
      "Encrypt your PDF with strong password protection and permission locks.",
    pdfCategory: "security",
    pdfCategoryLabel: "Security PDF",
    iconBgColor: "bg-amber-600/15",
    iconTextColor: "text-amber-600",
    iconName: "Lock",
    isPopular: true,
    actionType: "generic",
  },

  // --- VIEW & EXTRACT (11 Tools) ---
  {
    id: "pdf-viewer",
    name: "PDF Viewer",
    description:
      "Render and read PDF documents online with smooth zoom and page jumps.",
    pdfCategory: "view-extract",
    pdfCategoryLabel: "View & Extract",
    iconBgColor: "bg-blue-500/15",
    iconTextColor: "text-blue-500",
    iconName: "Eye",
    actionType: "generic",
  },
  {
    id: "pdf-thumbnail-generator",
    name: "PDF Thumbnail Generator",
    description:
      "Generate instant page thumbnail previews for grid gallery views.",
    pdfCategory: "view-extract",
    pdfCategoryLabel: "View & Extract",
    iconBgColor: "bg-orange-500/15",
    iconTextColor: "text-orange-500",
    iconName: "LayoutGrid",
    actionType: "generic",
  },
  {
    id: "pdf-metadata-viewer",
    name: "PDF Metadata Viewer",
    description:
      "View hidden metadata attributes including Author, Creator, Title, and Dates.",
    pdfCategory: "view-extract",
    pdfCategoryLabel: "View & Extract",
    iconBgColor: "bg-teal-500/15",
    iconTextColor: "text-teal-500",
    iconName: "Info",
    actionType: "generic",
  },
  {
    id: "pdf-metadata-editor",
    name: "PDF Metadata Editor",
    description:
      "Edit or sanitize title, author, producer, and subject metadata properties.",
    pdfCategory: "view-extract",
    pdfCategoryLabel: "View & Extract",
    iconBgColor: "bg-blue-500/15",
    iconTextColor: "text-purple-500",
    iconName: "FileEdit",
    actionType: "generic",
  },
  {
    id: "pdf-page-counter",
    name: "PDF Page Counter",
    description:
      "Count total page numbers, word estimates, and document complexity.",
    pdfCategory: "view-extract",
    pdfCategoryLabel: "View & Extract",
    iconBgColor: "bg-teal-500/15",
    iconTextColor: "text-teal-500",
    iconName: "Hash",
    actionType: "generic",
  },
  {
    id: "pdf-font-viewer",
    name: "PDF Font Viewer",
    description:
      "Inspect embedded font families, encoding types, and glyph tables.",
    pdfCategory: "view-extract",
    pdfCategoryLabel: "View & Extract",
    iconBgColor: "bg-indigo-500/15",
    iconTextColor: "text-indigo-500",
    iconName: "Type",
    actionType: "generic",
  },
  {
    id: "pdf-font-extractor",
    name: "PDF Font Extractor",
    description: "Extract embedded TrueType or OpenType font files from PDF.",
    pdfCategory: "view-extract",
    pdfCategoryLabel: "View & Extract",
    iconBgColor: "bg-fuchsia-500/15",
    iconTextColor: "text-fuchsia-500",
    iconName: "FileOutput",
    actionType: "generic",
  },
  {
    id: "pdf-information-viewer",
    name: "PDF Information Viewer",
    description:
      "Display PDF version format, encryption status, and permissions overview.",
    pdfCategory: "view-extract",
    pdfCategoryLabel: "View & Extract",
    iconBgColor: "bg-blue-600/15",
    iconTextColor: "text-blue-600",
    iconName: "Info",
    actionType: "generic",
  },
  {
    id: "pdf-text-extractor",
    name: "PDF Text Extractor (if text layer exists)",
    description:
      "Extract readable plain text content layer directly from vector PDF.",
    pdfCategory: "view-extract",
    pdfCategoryLabel: "View & Extract",
    iconBgColor: "bg-emerald-500/15",
    iconTextColor: "text-emerald-500",
    iconName: "FileText",
    actionType: "generic",
  },
  {
    id: "pdf-page-dimensions-viewer",
    name: "PDF Page Dimensions Viewer",
    description:
      "View exact page physical dimensions in mm, inches, points, and DPI.",
    pdfCategory: "view-extract",
    pdfCategoryLabel: "View & Extract",
    iconBgColor: "bg-cyan-500/15",
    iconTextColor: "text-cyan-500",
    iconName: "Maximize2",
    actionType: "generic",
  },
  {
    id: "pdf-page-ratio-checker",
    name: "PDF Page Ratio Checker",
    description: "Analyze page aspect ratios (A4, Letter, Legal, A3, Custom).",
    pdfCategory: "view-extract",
    pdfCategoryLabel: "View & Extract",
    iconBgColor: "bg-violet-500/15",
    iconTextColor: "text-violet-500",
    iconName: "Percent",
    actionType: "generic",
  },

  // --- OTHER PDF TOOLS (3 Tools) ---
  {
    id: "pdf-compare",
    name: "PDF Compare",
    description:
      "Compare two PDF files side-by-side to highlight visual or text diffs.",
    pdfCategory: "other",
    pdfCategoryLabel: "Other PDF Tools",
    iconBgColor: "bg-blue-500/15",
    iconTextColor: "text-blue-500",
    iconName: "Columns",
    actionType: "generic",
  },
  {
    id: "pdf-ocr-placeholder",
    name: "PDF OCR Placeholder",
    description:
      "Optical Character Recognition scanner placeholder for image-based PDFs.",
    pdfCategory: "other",
    pdfCategoryLabel: "Other PDF Tools",
    iconBgColor: "bg-blue-500/15",
    iconTextColor: "text-purple-500",
    iconName: "Scan",
    actionType: "generic",
  },
  {
    id: "pdf-hex-viewer",
    name: "PDF Hex Viewer",
    description:
      "Inspect raw binary stream, byte markers, and object catalog structure.",
    pdfCategory: "other",
    pdfCategoryLabel: "Other PDF Tools",
    iconBgColor: "bg-indigo-500/15",
    iconTextColor: "text-indigo-500",
    iconName: "Binary",
    actionType: "generic",
  },
];
